import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { beginSupportTab, endSupportTab, readSupportSession, saveSupportSession, supportRequest } from '../lib/supportSession';
import { getOperationsTodayQueue, synchronizeBrowserTenantContext } from '../lib/api';
import { NativeSupportBootstrap } from '../lib/nativeSupportBootstrap';

// Customer pages use the audited, tenant-locked transport. Platform administration
// requires a separate platform identity and is never part of a customer grant.
const UNAVAILABLE = ['/admin'];

export default function NativeSupportBoundary({ children }) {
  const [context, setContext] = useState(null);
  const [error, setError] = useState('');
  const [ended, setEnded] = useState(false);
  const started = useRef(false);
  const alive = useRef(true);
  const banner = useRef(null);
  const initialQueue = useRef(null);
  const queueController = useRef(null);
  const takeTodayQueue = useCallback(() => {
    const pending = initialQueue.current;
    initialQueue.current = null;
    return pending;
  }, []);
  const bootstrap = useMemo(() => ({ auth: context, takeTodayQueue }), [context, takeTodayQueue]);
  const [bannerHeight, setBannerHeight] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const stop = () => { alive.current = false; queueController.current?.abort(); initialQueue.current = null; setEnded(true); setContext(null); };
    window.addEventListener('aw-support-ended', stop);
    return () => window.removeEventListener('aw-support-ended', stop);
  }, []);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const code = new URLSearchParams(window.location.hash.slice(1)).get('code');
    if (location.pathname.startsWith('/support/')) window.history.replaceState(null, '', '/support/native');
    (async () => {
      if (code) {
        beginSupportTab(true);
        const session = await supportRequest('/exchange', { code });
        if (!alive.current) return;
        if (!session.native_workspace) throw new Error('This grant is not a native workspace session. Open a new session from Support.');
        saveSupportSession(session);
      }
      if (!readSupportSession()?.native_workspace) throw new Error('Open the customer workspace from Support to begin a new session.');
      if (['/', '/support/native', '/login'].includes(location.pathname)) {
        // Fetch the small first page concurrently with identity verification.
        // This still uses the normal tenant-locked, audited API. Nothing is
        // rendered until /context passes, and a rejection cannot be unhandled
        // while the boundary is still waiting for that verification.
        queueController.current = new AbortController();
        initialQueue.current = getOperationsTodayQueue(
          { search: '', offset: 0, limit: 25 }, { signal: queueController.current.signal, timeout: 15000 },
        ).then(response => ({ response }), error => ({ error }));
      }
      const auth = await supportRequest('/context');
      if (!alive.current) return;
      synchronizeBrowserTenantContext(auth);
      const expiresAt = new Date(auth.support_access.expires_at).getTime();
      if (!auth?.user?.user_id || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
        endSupportTab();
        throw new Error('Support identity could not be verified. Open a new session from Support.');
      }
      setContext(auth);
      if (location.pathname.startsWith('/support/') || location.pathname === '/login') navigate('/', { replace: true });
    })().catch((e) => {
      queueController.current?.abort(); initialQueue.current = null;
      if (alive.current) setError(e.message);
    });
  }, [location.pathname, navigate]);
  useEffect(() => {
    if (!context) return;
    const timer = setTimeout(() => { endSupportTab(); }, Math.max(0, new Date(context.support_access.expires_at).getTime() - Date.now()));
    return () => clearTimeout(timer);
  }, [context]);
  useEffect(() => {
    if (!banner.current) return;
    const measure = () => setBannerHeight(banner.current?.getBoundingClientRect().height || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(banner.current);
    return () => observer.disconnect();
  }, [context]);
  const close = async () => {
    // Unmount customer data immediately but retain the credential for the close request.
    alive.current = false; queueController.current?.abort(); initialQueue.current = null; setEnded(true); setContext(null);
    try { await supportRequest('/close', { close: true }); }
    catch { setError('Server closure could not be confirmed. Revoke this delegation in Support; this tab is ended.'); }
    finally { endSupportTab(); }
  };
  if (ended || error) return <main className="p-6"><h1>Support session ended or unavailable</h1><p role="alert">{error || 'Open a new session from the Support page.'}</p></main>;
  if (!context) return <p role="status" className="p-6">Opening customer workspace…</p>;
  const access = context.support_access;
  const unavailable = UNAVAILABLE.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  return <div style={{ '--aw-support-offset': `${bannerHeight}px` }}>
    <section ref={banner} className="sticky top-0 z-50 border-b border-border bg-warning-surface px-4 py-3 text-warning-foreground" aria-label="Delegated support access">
      <strong>Support · {access.tenant_name}</strong> · Operator: {access.staff_name} · {access.access_level}
      <span className="ml-3">Expires {new Date(access.expires_at).toLocaleTimeString()} · {access.reason}</span>
      <Button className="ml-3" variant="outline" onClick={close}>End support session</Button>
      <p className="text-sm">Access is limited to this customer organization. Actions are audited and follow its normal approval policies.</p>
    </section>
    {unavailable ? <main className="p-6"><p>This page is not available through delegated support access.</p><Button onClick={() => navigate('/')}>Back to customer workspace</Button></main> : <NativeSupportBootstrap.Provider value={bootstrap}>{children}</NativeSupportBootstrap.Provider>}
  </div>;
}
